/**
 * Pending Members List Screen
 * View all pre-registered family members
 */

import React, { useState, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
} from "react-native";
import { SafeAreaView, useSafeAreaInsets } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";
import { usePendingMembers } from "@/modules/pending-members";
import type { PendingMemberWithRelations } from "@/modules/pending-members";

export default function PendingMembersListScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const insets = useSafeAreaInsets();
  const {
    pendingMembers,
    isLoading,
    fetchPendingMembers,
    stats,
  } = usePendingMembers();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filter, setFilter] = useState<"all" | "pending" | "claimed">("all");

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await fetchPendingMembers();
    setIsRefreshing(false);
  }, [fetchPendingMembers]);

  // Filter members based on selection
  const filteredMembers = pendingMembers.filter((member) => {
    if (filter === "pending") return member.claimed_at === null;
    if (filter === "claimed") return member.claimed_at !== null;
    return true;
  });

  if (isLoading && pendingMembers.length === 0) {
    return (
      <View className="flex-1 items-center justify-center" style={{ backgroundColor: colors.background }}>
        <ActivityIndicator size="large" color="#DC2626" />
      </View>
    );
  }

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
      {/* Stats */}
      <View className="flex-row px-4 py-3 gap-2">
        <TouchableOpacity
          onPress={() => setFilter("all")}
          className="flex-1 rounded-xl p-3 items-center"
          style={{
            backgroundColor: filter === "all" ? "#DC262620" : colors.surface,
            borderWidth: filter === "all" ? 2 : 0,
            borderColor: "#DC2626",
          }}
        >
          <Text className="text-2xl font-bold" style={{ color: colors.text }}>
            {stats.total}
          </Text>
          <Text className="text-xs" style={{ color: colors.textSecondary }}>
            Totaal
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={() => setFilter("pending")}
          className="flex-1 rounded-xl p-3 items-center"
          style={{
            backgroundColor: filter === "pending" ? "#F5920520" : colors.surface,
            borderWidth: filter === "pending" ? 2 : 0,
            borderColor: "#F59205",
          }}
        >
          <Text className="text-2xl font-bold" style={{ color: "#F59205" }}>
            {stats.pending}
          </Text>
          <Text className="text-xs" style={{ color: colors.textSecondary }}>
            Wachtend
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={() => setFilter("claimed")}
          className="flex-1 rounded-xl p-3 items-center"
          style={{
            backgroundColor: filter === "claimed" ? "#10B98120" : colors.surface,
            borderWidth: filter === "claimed" ? 2 : 0,
            borderColor: "#10B981",
          }}
        >
          <Text className="text-2xl font-bold" style={{ color: "#10B981" }}>
            {stats.claimed}
          </Text>
          <Text className="text-xs" style={{ color: colors.textSecondary }}>
            Aangemeld
          </Text>
        </TouchableOpacity>
      </View>

      {/* List */}
      <ScrollView
        className="flex-1 px-4"
        refreshControl={
          <RefreshControl refreshing={isRefreshing} onRefresh={handleRefresh} />
        }
      >
        {filteredMembers.length === 0 ? (
          <View className="items-center py-12">
            <Ionicons
              name="people-outline"
              size={48}
              color={colors.textSecondary}
            />
            <Text className="mt-4 text-lg" style={{ color: colors.textSecondary }}>
              {filter === "all"
                ? "Nog geen pre-registraties"
                : filter === "pending"
                ? "Geen wachtende leden"
                : "Nog niemand aangemeld"}
            </Text>
            {filter === "all" && (
              <TouchableOpacity
                onPress={() => router.push("/(super-admin)/pending-members/create")}
                className="mt-4 px-6 py-3 rounded-xl flex-row items-center"
                style={{ backgroundColor: "#DC2626" }}
              >
                <Ionicons name="add" size={20} color="#FFFFFF" />
                <Text className="ml-2 text-white font-medium">
                  Voeg familielid toe
                </Text>
              </TouchableOpacity>
            )}
          </View>
        ) : (
          filteredMembers.map((member) => (
            <MemberCard
              key={member.id}
              member={member}
              onPress={() => router.push(`/(super-admin)/pending-members/${member.id}`)}
            />
          ))
        )}

        {/* Bottom spacer for FAB */}
        <View className="h-24" />
      </ScrollView>

      {/* FAB - Add member */}
      <TouchableOpacity
        onPress={() => router.push("/(super-admin)/pending-members/create")}
        className="absolute right-6 w-14 h-14 rounded-full items-center justify-center shadow-lg"
        style={{ 
          backgroundColor: "#DC2626",
          bottom: Math.max(insets.bottom, 16) + 16,
        }}
      >
        <Ionicons name="add" size={28} color="#FFFFFF" />
      </TouchableOpacity>
    </SafeAreaView>
  );
}

/**
 * Member Card Component
 */
function MemberCard({
  member,
  onPress,
}: {
  member: PendingMemberWithRelations;
  onPress: () => void;
}) {
  const colors = useThemeColors();
  const isClaimed = member.claimed_at !== null;

  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.7}
      className="rounded-xl p-4 mb-3"
      style={{ backgroundColor: colors.surface }}
    >
      <View className="flex-row items-center">
        {/* Status indicator */}
        <View
          className="w-10 h-10 rounded-full items-center justify-center mr-3"
          style={{ backgroundColor: isClaimed ? "#10B98120" : "#F5920520" }}
        >
          <Ionicons
            name={isClaimed ? "checkmark-circle" : "time-outline"}
            size={24}
            color={isClaimed ? "#10B981" : "#F59205"}
          />
        </View>

        {/* Info */}
        <View className="flex-1">
          <Text className="font-semibold text-base" style={{ color: colors.text }}>
            {member.full_name}
          </Text>
          <Text className="text-sm" style={{ color: colors.textSecondary }}>
            {member.email}
          </Text>
          {member.team && (
            <View className="flex-row items-center mt-1">
              <View
                className="w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: member.team.color }}
              />
              <Text className="text-xs" style={{ color: colors.textSecondary }}>
                {member.team.name}
                {member.is_captain && " (Kapitein)"}
              </Text>
            </View>
          )}
        </View>

        {/* Arrow */}
        <Ionicons name="chevron-forward" size={20} color={colors.textSecondary} />
      </View>

      {/* Claimed info */}
      {isClaimed && member.claimed_by_profile && (
        <View
          className="mt-3 pt-3 flex-row items-center"
          style={{ borderTopWidth: 1, borderTopColor: colors.border }}
        >
          <Ionicons name="person-circle-outline" size={16} color="#10B981" />
          <Text className="ml-2 text-xs" style={{ color: "#10B981" }}>
            Aangemeld als: {member.claimed_by_profile.full_name || member.claimed_by_profile.email}
          </Text>
        </View>
      )}
    </TouchableOpacity>
  );
}

